import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";

/**
 * Per-Persona affiliate status. Each Persona profile is its own affiliate
 * identity, so the status is resolved against the active Persona.
 *
 * `eligible_now` collapses four rules:
 *   • super-affiliate flag (super-admin Personas) → always eligible
 *   • admin override on the profile → eligible
 *   • has ever paid + currently active subscription → eligible
 *   • everyone else → not eligible (locked or paused)
 */
export type AffiliateStatus = {
  profile_id: string;
  user_id: string;
  has_ever_paid: boolean;
  has_active_paid_sub: boolean;
  override_eligible: boolean;
  is_enrolled: boolean;
  enrollment_status: "enrolled" | "paused" | null;
  is_super_affiliate: boolean;
  eligible_now: boolean;
  referral_username: string | null;
};

export function useAffiliateStatus(profileIdOverride?: string | null) {
  const { user } = useAuth();
  const { activePersonaId } = useWorkspace();
  const profileId = profileIdOverride ?? activePersonaId ?? null;
  const [status, setStatus] = useState<AffiliateStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      if (typeof (supabase as unknown as { rpc?: unknown }).rpc !== "function") {
        setStatus(null);
        return;
      }
      const args: { _profile_id?: string } = {};
      if (profileId) args._profile_id = profileId;
      const { data, error } = await supabase.rpc("get_affiliate_status", args);
      if (error) {
        console.error("[affiliate] status error", error);
        setStatus(null);
      } else {
        const row = Array.isArray(data) ? data[0] : data;
        setStatus(row ? (row as unknown as AffiliateStatus) : null);
      }
    } catch (err) {
      console.error("[affiliate] status threw", err);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [user, profileId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { status, loading, refresh };
}
